// Durable, reload-surviving upload queue for the AI Interview session audio.
//
// The recording used to be uploaded fire-and-forget from finalize() — a plain
// in-memory blob + a background POST. If the candidate reloaded, closed the
// tab, or the OS backgrounded the page (very common right after the "Thank
// you" screen appears, since completeSession/fetchReport land in ~1-3s while a
// multi-MB audio upload is still in flight), the POST was aborted and the
// recording was lost forever — it only ever existed in that tab's memory.
//
// Fix: the moment recording stops we persist the blob to IndexedDB (which
// survives reloads AND tab close), then attempt the upload. On success we
// delete it; on failure it stays queued. Every AI-Interview page load, every
// 'online' event, and a periodic tick re-drain the queue — so the upload keeps
// getting retried until it succeeds, as long as the candidate's browser opens
// our app again with a network connection.
//
// The scoped invite JWT lives in sessionStorage (lost on tab close), so we
// snapshot it into the queued record too — that way a drain triggered after a
// full tab-close/reopen (within the token's ~12h life) still authenticates.

import { uploadSessionAudio } from './aiInterviewAPI'

const DB_NAME = 'ai_interview_uploads'
const STORE = 'pending'
const VERSION = 1

const idbAvailable = () => typeof indexedDB !== 'undefined'

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(record) {
  return openDb().then((db) => new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(record)
    tx.oncomplete = () => res()
    tx.onerror = () => rej(tx.error)
    tx.onabort = () => rej(tx.error)
  }))
}

function idbGetAll() {
  return openDb().then((db) => new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readonly')
    const r = tx.objectStore(STORE).getAll()
    r.onsuccess = () => res(r.result || [])
    r.onerror = () => rej(r.error)
  }))
}

function idbDelete(id) {
  return openDb().then((db) => new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(id)
    tx.oncomplete = () => res()
    tx.onerror = () => rej(tx.error)
  }))
}

// Save a recording to durable storage so its upload survives a reload / tab
// close. Keyed by assessmentAssignedId (one recording per interview, so a
// re-finalize just overwrites). Returns true if it was persisted.
export const enqueueRecording = async ({ assessmentAssignedId, blob, mimeType, chapters, authToken }) => {
  if (!idbAvailable() || !assessmentAssignedId || !blob) return false
  try {
    await idbPut({
      id: assessmentAssignedId,
      blob,
      mimeType: mimeType || (blob && blob.type) || '',
      chapters: chapters || [],
      authToken: authToken || null,
      createdAt: Date.now(),
    })
    return true
  } catch (e) {
    console.warn('[upload-queue] enqueue failed (non-fatal):', e?.message || e)
    return false
  }
}

let draining = false
// Attempt to upload every pending recording. Safe to call repeatedly; a
// success removes the record, a failure (offline, 401, network) leaves it
// queued for the next trigger. Never throws.
export const drainPendingUploads = async () => {
  if (!idbAvailable() || draining) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  draining = true
  try {
    const items = await idbGetAll().catch(() => [])
    for (const it of items) {
      try {
        // Explicit Authorization from the snapshot so a drain after tab-close
        // (empty sessionStorage) still authenticates. studentRequest gives a
        // per-request header precedence over its sessionStorage fallback.
        const extraConfig = it.authToken ? { headers: { Authorization: it.authToken } } : undefined
        // eslint-disable-next-line no-await-in-loop
        await uploadSessionAudio(it.id, it.blob, it.mimeType, it.chapters, extraConfig)
        // eslint-disable-next-line no-await-in-loop
        await idbDelete(it.id).catch(() => {})
      } catch (e) {
        // Keep it queued — the next app load / 'online' / tick retries it.
        console.warn('[upload-queue] upload kept queued for retry:', it.id, e?.message || e)
      }
    }
  } finally {
    draining = false
  }
}

let wired = false
// Register global resume triggers exactly once: retry now, on reconnect, and
// on a periodic tick (covers a tab that stayed open through an outage).
export const initUploadQueueResumption = () => {
  if (wired || typeof window === 'undefined') return
  wired = true
  const kick = () => { drainPendingUploads() }
  window.addEventListener('online', kick)
  // ponytail: 30s poll is a simple safety net; upgrade to backoff if the
  // queue ever holds many items or uploads are large enough to overlap.
  setInterval(kick, 30000)
  kick()
}
